import React, { useEffect, useMemo, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import axios from "axios";
import {
  useGetTicketTagSLAEscalationLevelsQuery,
  useStatusQuery,
} from "../../Services/settingsAPIs/CommonAPI";

type SlTypeValue = "workflow" | "priority" | "tag";

const PRIORITY_OPTIONS = [
  { label: "Urgent", value: "urgent" },
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];

interface IProps {
  closeFilterModal: () => void;
  selectedFilterAndValue: any;
  setSelectedFilterAndValue: any;
  handleSubmit: (selectedFilterAndValue: any) => void;
  taskSource: "emails" | "tickets";
  selectedSlType?: SlTypeValue;
  selectedWorkFlow?: string;
}

const SLBreachFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectedFilterAndValue,
  setSelectedFilterAndValue,
  handleSubmit,
  taskSource,
  selectedSlType = "workflow",
  selectedWorkFlow = "",
}) => {
  const [selectedUnassignedFilters, setSelectedUnassignedFilters] = useState<
    string[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);

  const [initialFilters, setInitialFilters] = useState<string[]>([]);
  const [initialFilterValues, setInitialFilterValues] = useState<any>({});

  const { data: ticketTags } = useGetTicketTagSLAEscalationLevelsQuery(
    {},
    { skip: taskSource !== "tickets" || selectedSlType !== "tag" }
  );

  const tagOptions = useMemo(() => {
    if (!Array.isArray(ticketTags)) return [];
    return ticketTags
      .map((tag: any) => ({
        id: String(tag.tag_id ?? tag.id ?? ""),
        name: tag.tag ?? tag.tag_name ?? tag.name ?? "Unknown tag",
      }))
      .filter((tag) => tag.id);
  }, [ticketTags]);

  const showWorkflowStageFilter =
    taskSource === "tickets" &&
    selectedSlType === "workflow" &&
    Boolean(selectedWorkFlow);

  const { data: workflowStatusData, isLoading: workflowStagesLoading } =
    useStatusQuery(selectedWorkFlow, {
      skip: !showWorkflowStageFilter,
    });

  const workflowStageOptions = useMemo(() => {
    if (!Array.isArray(workflowStatusData?.all_statuses)) return [];

    return workflowStatusData.all_statuses
      .map((stage: any) => {
        const statusType = String(stage.status_type ?? "").toLowerCase();

        if (statusType === "active" || statusType === "reopen") {
          const label =
            typeof stage.status === "string"
              ? stage.status
              : statusType === "active"
                ? "Open"
                : "Reopen";

          return {
            label: String(label).trim(),
            value: `${statusType}|null`,
          };
        }

        const label = stage.status?.status ?? "";
        const statusId = stage.status?.id;
        if (!label || statusId == null) return null;

        return {
          label: String(label).trim(),
          value: `wip|${statusId}`,
        };
      })
      .filter(
        (stage): stage is { label: string; value: string } =>
          Boolean(stage?.label && stage?.value),
      );
  }, [workflowStatusData]);

  const baseTicketFilters = [
    { label: "Ticket ID", value: "id" },
    { label: "Subject / Title", value: "subject" },
    { label: "Customer Name", value: "customer_name" },
    { label: "Assignee", value: "assignee" },
  ];

  const emailFilters = [
    { label: "Subject / Title", value: "subject" },
    { label: "Customer Name", value: "customer_name" },
    { label: "Assignee", value: "assignee" },
  ];

  const filters = useMemo(() => {
    if (taskSource === "emails") return emailFilters;

    const list = [...baseTicketFilters];
    if (selectedSlType === "priority") {
      list.push({ label: "Priority", value: "priority" });
    } else if (selectedSlType === "tag") {
      list.push({ label: "Tag", value: "tag_id" });
    } else if (showWorkflowStageFilter) {
      list.push({ label: "Workflow Stage", value: "workflow_stage" });
    }
    return list;
  }, [taskSource, selectedSlType, showWorkflowStageFilter]);

  const filterStorageKey = `slBreachSelectedFilters_${taskSource}`;
  const filterValuesStorageKey = `slBreachSelectedFilterValues_${taskSource}`;

  useEffect(() => {
    const savedFilters = sessionStorage.getItem(filterStorageKey);
    const savedFilterValues = sessionStorage.getItem(filterValuesStorageKey);

    if (savedFilters && savedFilterValues) {
      const parsedFilters = JSON.parse(savedFilters);
      const parsedValues = JSON.parse(savedFilterValues);
      const allowed = parsedFilters.filter((f: string) =>
        filters.some((fl) => fl.value === f)
      );

      setSelectedUnassignedFilters(allowed);
      setSelectedFilterAndValue({
        ...parsedValues,
        priority: selectedSlType === "priority" ? parsedValues.priority ?? "" : "",
        tag_id: selectedSlType === "tag" ? parsedValues.tag_id ?? "" : "",
        workflow_stage:
          showWorkflowStageFilter ? parsedValues.workflow_stage ?? "" : "",
        status_type:
          showWorkflowStageFilter ? parsedValues.status_type ?? "" : "",
        status_id: showWorkflowStageFilter ? parsedValues.status_id ?? "" : "",
      });
      setInitialFilters(allowed);
      setInitialFilterValues(parsedValues);
    } else {
      const appliedFilters = Object.keys(selectedFilterAndValue).filter(
        (key) => selectedFilterAndValue[key]
      );
      const allowed = appliedFilters.filter((f) =>
        filters.some((fl) => fl.value === f)
      );

      setSelectedUnassignedFilters(allowed);
      setInitialFilters(allowed);
      setInitialFilterValues(selectedFilterAndValue);
    }
  }, [taskSource, selectedSlType, showWorkflowStageFilter]);

  useEffect(() => {
    setSelectedUnassignedFilters((prev) =>
      prev.filter((f) => filters.some((fl) => fl.value === f))
    );
    setSelectedFilterAndValue((prev: any) => ({
      ...prev,
      priority: selectedSlType === "priority" ? prev.priority ?? "" : "",
      tag_id: selectedSlType === "tag" ? prev.tag_id ?? "" : "",
      workflow_stage: showWorkflowStageFilter ? prev.workflow_stage ?? "" : "",
      status_type: showWorkflowStageFilter ? prev.status_type ?? "" : "",
      status_id: showWorkflowStageFilter ? prev.status_id ?? "" : "",
    }));
  }, [selectedSlType, showWorkflowStageFilter, filters, setSelectedFilterAndValue]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedUnassignedFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectedFilterAndValue((prev: any) => ({
          ...prev,
          [value]: "",
          ...(value === "workflow_stage"
            ? { status_type: "", status_id: "" }
            : {}),
        }));
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedFilterAndValue((prevFilterValues: any) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleWorkflowStageChange = (stageValue: string) => {
    if (!stageValue) {
      setSelectedFilterAndValue((prev: any) => ({
        ...prev,
        workflow_stage: "",
        status_type: "",
        status_id: "",
      }));
      return;
    }

    const [statusType, statusId = "null"] = stageValue.split("|");
    setSelectedFilterAndValue((prev: any) => ({
      ...prev,
      workflow_stage: stageValue,
      status_type: statusType,
      status_id: statusId,
    }));
  };

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchAssigneeSuggestions = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/users/users/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { username__icontains: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setAssigneeSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch assignee suggestions.");
      });
  };

  const emptyFilters = useMemo(
    () =>
      filters.reduce((acc, filter) => {
        acc[filter.value] = "";
        return acc;
      }, {} as Record<string, string>),
    [filters]
  );

  const resetFilters = () => {
    setSelectedUnassignedFilters([]);
    setSelectedFilterAndValue(emptyFilters);
    setError(null);
    setInitialFilters([]);
    setInitialFilterValues(emptyFilters);
    sessionStorage.removeItem(filterStorageKey);
    sessionStorage.removeItem(filterValuesStorageKey);
    handleSubmit({});
  };

  const isApplyEnabled = selectedUnassignedFilters.some((filter) => {
    const value = selectedFilterAndValue[filter];
    return value && value.toString().trim() !== "";
  });

  const cleanEmptyFiltersAndClose = () => {
    setSelectedUnassignedFilters(initialFilters);
    setSelectedFilterAndValue(initialFilterValues);
    closeFilterModal();
  };

  const handleApplyFilters = () => {
    const nextFilters = selectedUnassignedFilters.filter((filter) => {
      const value = selectedFilterAndValue[filter];
      return value && value.toString().trim() !== "";
    });

    const filterValuesToSubmit = nextFilters.reduce((acc, filter) => {
      if (filter === "workflow_stage") {
        const stageValue = selectedFilterAndValue.workflow_stage;
        if (stageValue) {
          acc.workflow_stage = stageValue;
          const [statusType, statusId = "null"] = String(stageValue).split("|");
          acc.status_type = statusType;
          acc.status_id = statusId;
        }
        return acc;
      }

      acc[filter] = selectedFilterAndValue[filter];
      return acc;
    }, {} as Record<string, string>);

    setSelectedUnassignedFilters(nextFilters);
    setSelectedFilterAndValue(filterValuesToSubmit);

    sessionStorage.setItem(filterStorageKey, JSON.stringify(nextFilters));
    sessionStorage.setItem(
      filterValuesStorageKey,
      JSON.stringify(filterValuesToSubmit)
    );

    handleSubmit(filterValuesToSubmit);
    closeFilterModal();
  };

  const renderFilterInput = (filter: { label: string; value: string }) => {
    if (filter.value === "assignee") {
      return (
        <Autocomplete
          freeSolo
          options={assigneeSuggestions.map((option) => option.username)}
          value={selectedFilterAndValue[filter.value] || ""}
          onInputChange={(_event, newInputValue) => {
            searchAssigneeSuggestions(newInputValue);
          }}
          onChange={(_event, newValue) => {
            const selectedSuggestion = assigneeSuggestions.find(
              (suggestion) => suggestion.username === newValue
            );

            setSelectedFilterAndValue((prev: any) => ({
              ...prev,
              assignee: selectedSuggestion ? selectedSuggestion.username : "",
            }));
          }}
          renderInput={(params) => (
            <TextField {...params} label={filter.label} variant="outlined" />
          )}
        />
      );
    }

    if (filter.value === "priority") {
      return (
        <FormControl fullWidth>
          <InputLabel id="sl-breach-priority-label">Priority</InputLabel>
          <Select
            labelId="sl-breach-priority-label"
            label="Priority"
            value={selectedFilterAndValue.priority || ""}
            onChange={(e) =>
              handleFilterValueChange(String(e.target.value), "priority")
            }
          >
            <MenuItem value="">
              <em>Select priority</em>
            </MenuItem>
            {PRIORITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (filter.value === "tag_id") {
      return (
        <FormControl fullWidth>
          <InputLabel id="sl-breach-tag-label">Tag</InputLabel>
          <Select
            labelId="sl-breach-tag-label"
            label="Tag"
            value={selectedFilterAndValue.tag_id || ""}
            onChange={(e) =>
              handleFilterValueChange(String(e.target.value), "tag_id")
            }
          >
            <MenuItem value="">
              <em>Select tag</em>
            </MenuItem>
            {tagOptions.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (filter.value === "workflow_stage") {
      return (
        <FormControl fullWidth>
          <InputLabel id="sl-breach-workflow-stage-label">Workflow status</InputLabel>
          <Select
            labelId="sl-breach-workflow-stage-label"
            label="Workflow status"
            value={selectedFilterAndValue.workflow_stage || ""}
            onChange={(e) =>
              handleWorkflowStageChange(String(e.target.value))
            }
          >
            <MenuItem value="">
              <em>
                {workflowStagesLoading ? "Loading statuses..." : "Select workflow status"}
              </em>
            </MenuItem>
            {workflowStageOptions.map((stage) => (
              <MenuItem key={stage.value} value={stage.value}>
                {stage.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        label={filter.label}
        fullWidth
        value={selectedFilterAndValue[filter.value] || ""}
        onChange={(e) => handleFilterValueChange(e.target.value, filter.value)}
      />
    );
  };

  return (
    <Modal
      show={true}
      onHide={cleanEmptyFiltersAndClose}
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="fw-bold">
          Filters - {taskSource === "tickets" ? "Tickets" : "Emails"}
        </Modal.Title>
        <CloseButton onClick={cleanEmptyFiltersAndClose} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnassignedFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectedUnassignedFilters.includes(filter.value) &&
                  renderFilterInput(filter)}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleApplyFilters}
          disabled={!isApplyEnabled}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SLBreachFilterModal;
